import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { ZisterneData, ZisterneHistoryPoint } from '../version.service';
import { updatedAgo as fmtAgo } from '../shared/format.utils';
import { chartPalette, dragZoom, timeAxisTick, relativeDayTick } from '../shared/chart.utils';

@Component({
  selector: 'app-zisterne',
  templateUrl: './zisterne.component.html',
})
export class ZisterneComponent implements OnChanges {
  @Input() zisterne: ZisterneData | null = null;
  @Input() zisterneHistory: ZisterneHistoryPoint[] = [];
  @Input() zisterneFlash = false;
  @Input() liveDotOn = true;
  @Input() zisterneUpdatedAt: number | null = null;
  @Input() nowTick = Date.now();
  @Input() darkMode = false;

  levelChartData: ChartData<'line'> = { datasets: [] };
  levelChartOptions: ChartConfiguration<'line'>['options'] = {};
  levelExpandedData: ChartData<'line'> = { datasets: [] };
  levelExpandedOptions: ChartConfiguration<'line'>['options'] = {};

  // Expanded-chart range. 'week' is the live 7-day history already in memory;
  // day/month/year fetch the downsampled long-term archive from /api/history.
  range: 'day' | 'week' | 'month' | 'year' = 'week';
  rangeLoading = false;
  rangeHistory: ZisterneHistoryPoint[] = [];
  readonly chartRanges: { key: 'day' | 'week' | 'month' | 'year'; label: string }[] = [
    { key: 'day',   label: 'Tag' },
    { key: 'week',  label: 'Woche' },
    { key: 'month', label: 'Monat' },
    { key: 'year',  label: 'Jahr' },
  ];
  private static readonly RANGE_MS: Record<string, number> = {
    day:   24 * 3600 * 1000,
    week:  7 * 24 * 3600 * 1000,
    month: 30 * 24 * 3600 * 1000,
    year:  365 * 24 * 3600 * 1000,
  };

  get displayHistory(): ZisterneHistoryPoint[] {
    return this.range === 'week' ? this.zisterneHistory : this.rangeHistory;
  }

  updatedAgo(ts: number | null): string { return fmtAgo(ts, this.nowTick); }

  setRange(range: 'day' | 'week' | 'month' | 'year'): void {
    this.range = range;
    if (range === 'week') { this.buildExpanded(); return; }
    const to   = Date.now();
    const from = to - ZisterneComponent.RANGE_MS[range];
    this.rangeLoading = true;
    fetch(`/api/history?series=zisterne&from=${from}&to=${to}&buckets=600`)
      .then(r => r.json())
      .then((pts: any[]) => { this.rangeHistory = pts ?? []; this.rangeLoading = false; this.buildExpanded(); })
      .catch(() => { this.rangeLoading = false; });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Skip rebuilds triggered solely by the nowTick/liveDotOn ticks.
    if (!changes['zisterneHistory'] && !changes['darkMode']) return;
    if (this.zisterneHistory.length >= 2) {
      this.levelChartData    = this.levelData(this.zisterneHistory);
      this.levelChartOptions = this.smallOptions();
    }
    this.buildExpanded();
  }

  private buildExpanded(): void {
    const pts = this.displayHistory;
    if (pts.length < 1) { this.levelExpandedData = { datasets: [] }; return; }
    this.levelExpandedData    = this.levelData(pts);
    this.levelExpandedOptions = this.expandedOptions(pts);
  }

  private levelData(pts: ZisterneHistoryPoint[]): ChartData<'line'> {
    return {
      datasets: [{
        label: 'Füllstand',
        data: pts.map(p => ({ x: p.ts, y: p.levelPct })),
        borderColor: '#0d6efd',
        backgroundColor: 'rgba(13,110,253,0.15)',
        fill: true,
      }],
    };
  }

  /** Small card chart: fixed 7-day window with weekday ticks. */
  private smallOptions(): ChartConfiguration<'line'>['options'] {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    return {
      ...this.commonOptions(false),
      scales: {
        x: {
          display: true, type: 'linear',
          min: Date.now() - 7 * 24 * 3600 * 1000, max: Date.now(),
          ticks: {
            color: textColor, maxTicksLimit: 3, font: { size: 10 }, maxRotation: 0,
            callback: (val) => new Date(val as number).toLocaleDateString('de-DE', { weekday: 'short' }),
          },
          grid: { display: false, color: gridColor },
        },
        y: this.yScale(false),
      },
    };
  }

  /** Expanded chart: axis spans the full selected range window (now-windowMs … now). */
  private expandedOptions(pts: ZisterneHistoryPoint[]): ChartConfiguration<'line'>['options'] {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    const xMax = Date.now();
    const xMin = xMax - ZisterneComponent.RANGE_MS[this.range];
    const xTick = this.range === 'day' ? relativeDayTick : timeAxisTick(xMin, xMax);
    return {
      ...this.commonOptions(true),
      scales: {
        x: {
          display: true, type: 'linear', min: xMin, max: xMax,
          ticks: { color: textColor, maxTicksLimit: 8, font: { size: 11 }, maxRotation: 0,
                   callback: xTick },
          grid: { display: true, color: gridColor },
        },
        y: this.yScale(true),
      },
    };
  }

  private yScale(expanded: boolean): any {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    return {
      display: true, min: 0, max: 100,
      ticks: { color: textColor, maxTicksLimit: expanded ? 6 : 4, font: { size: expanded ? 11 : 10 },
               callback: (val: any) => `${val}%` },
      grid: { color: gridColor, display: expanded },
      title: { display: expanded, text: 'Füllstand (%)', color: textColor, font: { size: 11 } },
    };
  }

  private commonOptions(expanded: boolean): ChartConfiguration<'line'>['options'] {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 4, right: 6, bottom: 0, left: 0 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const ts = items[0]?.parsed.x;
              if (ts == null) return '';
              return new Date(ts).toLocaleString('de-DE', {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              });
            },
            label: (item) => ` ${(item.parsed.y ?? 0).toFixed(1)} %`,
          },
        },
        ...(expanded ? { zoom: dragZoom() } : {}),
      },
      elements: {
        point: { radius: 0, hoverRadius: 4 },
        line: { borderWidth: expanded ? 2 : 1.5, tension: 0.4 },
      },
    };
  }
}
